import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ProjectContext } from 'contexts/ProjectContext';
import { TextInput } from 'components/lib';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ValidationContext } from 'contexts/ValidationContext';
import {
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { geocodeAddress, geocodePlaceId, getPlaceAutocomplete, type GooglePlacePrediction } from 'api/googleApi';
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import * as turf from '@turf/turf';
import switzerland from 'data/switzerlandCoordinates.json';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';

const MIN_ADDRESS_LENGTH = 3;
const DEFAULT_MAP_CENTER = { lat: 47.3769, lng: 8.5417 };
export const PROJECT_LOCATION_RADIUS_METERS = 100;
const DEFAULT_MAP_ZOOM = 14;
const PROJECT_LOCATION_FOCUS_ZOOM = 17;
const SWITZERLAND_POLYGON = turf.polygon(switzerland.features[0].geometry.coordinates);

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const MapUpdater: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [center, map, zoom]);

  return null;
};

const MapClickHandler: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
};

const formatCoordinateValue = (value: string | number | null | undefined) => {
  const parsedValue = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsedValue) ? parsedValue.toFixed(5) : '';
};

const formatCoordinatesInput = (lat: string | number | null | undefined, lng: string | number | null | undefined) => {
  const formattedLat = formatCoordinateValue(lat);
  const formattedLng = formatCoordinateValue(lng);

  if (!formattedLat || !formattedLng) {
    return '';
  }

  return `${formattedLat}, ${formattedLng}`;
};

const parseCoordinateInput = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue || /[a-z]/i.test(trimmedValue)) {
    return null;
  }

  const normalizedValue = trimmedValue.replace(/\s+/g, '');
  const match = normalizedValue.match(/^(-?\d+(?:[.,]\d+)?),(-?\d+(?:[.,]\d+)?)$/);
  if (!match) {
    return null;
  }

  const lat = Number.parseFloat(match[1].replace(',', '.'));
  const lng = Number.parseFloat(match[2].replace(',', '.'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const MandatoryFields: React.FC = () => {
  const {
    project,
    setProject,
    // handleChangeAnalysisType
  } = useContext(ProjectContext);

  const {
    isProjectNameValid,
    setIsProjectNameValid,
    projectNameErrorMessage,
    setProjectNameErrorMessage,
    isProjectMunicipalityValid,
    setIsProjectMunicipalityValid,
    projectMunicipalityErrorMessage,
    setProjectMunicipalityErrorMessage,
    showProjectValidationErrors,
  } = useContext(ValidationContext);

  const [addressValue, setAddressValue] = useState(
    project?.address || formatCoordinatesInput(project?.coordinates?.lat, project?.coordinates?.lng)
  );
  const [isListOpen, setIsListOpen] = useState(false);
  const [placePredictions, setPlacePredictions] = useState<GooglePlacePrediction[]>([]);
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false);
  const addressDropdownRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRequestRef = useRef(0);

  const handleInputsChange = (name: string, value: any) => {
    if (name === 'name') {
      if (value === '') {
        setIsProjectNameValid(false);
        setProjectNameErrorMessage('Please enter a project name!');
      } else {
        setIsProjectNameValid(true);
        setProjectNameErrorMessage('');
      }
    }

    setProject({ ...project, [name]: value });
  };

  const handleProjectSpecNumberChange = (
    field: 'numberOfBuildings' | 'grossFloorSurface' | 'numberOfFloors',
    value: string | number
  ) => {
    if (value === '') {
      if (field !== 'numberOfBuildings') {
        setProject((current) => ({ ...current, [field]: null }));
      }
      return;
    }

    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return;

    const normalizedValue =
      field === 'grossFloorSurface' ? Math.max(0, parsedValue) : Math.max(1, Math.floor(parsedValue));
    setProject((current) => ({ ...current, [field]: normalizedValue }));
  };

  const handleAddressSelect = async (value: string, placeId?: string) => {
    setAddressValue(value);
    setIsListOpen(false);
    setPlacePredictions([]);
    setProject((prevProject) => ({ ...prevProject, address: value }));

    const parsedCoordinates = parseCoordinateInput(value);
    if (parsedCoordinates) {
      setProject((prevProject: typeof project) => ({
        ...prevProject,
        coordinates: {
          ...(prevProject.coordinates || {}),
          lat: String(parsedCoordinates.lat),
          lng: String(parsedCoordinates.lng),
        },
      }));
      return;
    }

    try {
      const data = placeId ? await geocodePlaceId(placeId) : await geocodeAddress(value);
      const location = data?.results?.[0]?.geometry?.location;
      if (location?.lat != null && location?.lng != null) {
        setProject((prevProject: typeof project) => ({
          ...prevProject,
          coordinates: {
            ...(prevProject.coordinates || {}),
            lat: String(location.lat),
            lng: String(location.lng),
          },
        }));
      }
    } catch (error) {
      console.error('Failed to fetch coordinates for address', error);
    }
  };

  const handleAddressChange = (value: string) => {
    setAddressValue(value);
    setProject((prevProject) => ({ ...prevProject, address: value }));

    const parsedCoordinates = parseCoordinateInput(value);
    if (parsedCoordinates) {
      setProject((prevProject) => ({
        ...prevProject,
        coordinates: {
          ...(prevProject.coordinates || {}),
          lat: String(parsedCoordinates.lat),
          lng: String(parsedCoordinates.lng),
        },
      }));
      setIsListOpen(false);
      setPlacePredictions([]);
      setIsPlacePredictionsLoading(false);
      return;
    }

    const trimmed = value.trim();
    if (trimmed.length >= MIN_ADDRESS_LENGTH) {
      setIsListOpen(true);
    } else {
      setIsListOpen(false);
      setPlacePredictions([]);
    }
  };

  const handleMapPick = (lat: number, lng: number) => {
    const nextCoordinateValue = formatCoordinatesInput(lat, lng);
    setAddressValue(nextCoordinateValue);
    setProject((prevProject) => ({
      ...prevProject,
      address: '',
      coordinates: {
        ...(prevProject.coordinates || {}),
        lat: lat.toFixed(6),
        lng: lng.toFixed(6),
      },
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (addressDropdownRef.current && !addressDropdownRef.current.contains(target)) {
        setIsListOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmedAddress = addressValue.trim();
    if (parseCoordinateInput(trimmedAddress)) {
      setIsPlacePredictionsLoading(false);
      setPlacePredictions([]);
      return;
    }

    if (trimmedAddress.length < MIN_ADDRESS_LENGTH) {
      setIsPlacePredictionsLoading(false);
      setPlacePredictions([]);
      return;
    }

    setIsPlacePredictionsLoading(true);
    const requestId = autocompleteRequestRef.current + 1;
    autocompleteRequestRef.current = requestId;
    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await getPlaceAutocomplete(trimmedAddress, 'ch');
        if (autocompleteRequestRef.current !== requestId) return;
        setPlacePredictions(Array.isArray(data?.predictions) ? data.predictions : []);
      } catch (error) {
        if (autocompleteRequestRef.current !== requestId) return;
        setPlacePredictions([]);
        console.error('Failed to fetch place predictions', error);
      } finally {
        if (autocompleteRequestRef.current === requestId) {
          setIsPlacePredictionsLoading(false);
        }
      }
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [addressValue]);

  const latRaw = String(project.coordinates?.lat ?? '').trim();
  const lngRaw = String(project.coordinates?.lng ?? '').trim();
  const hasLat = latRaw !== '';
  const hasLng = lngRaw !== '';
  const latValue = Number.parseFloat(latRaw.replace(',', '.'));
  const lngValue = Number.parseFloat(lngRaw.replace(',', '.'));
  const latIsValid = !hasLat || (Number.isFinite(latValue) && latValue >= -90 && latValue <= 90);
  const lngIsValid = !hasLng || (Number.isFinite(lngValue) && lngValue >= -180 && lngValue <= 180);
  const coordinatesWithinSwitzerland =
    hasLat &&
    hasLng &&
    latIsValid &&
    lngIsValid &&
    turf.booleanPointInPolygon(turf.point([lngValue, latValue]), SWITZERLAND_POLYGON);
  const coordinatesValid = hasLat && hasLng && latIsValid && lngIsValid && coordinatesWithinSwitzerland;
  const shouldShowCoordinatesError = (hasLat || hasLng) && !coordinatesValid;
  const showCoordinatesError = showProjectValidationErrors && shouldShowCoordinatesError;
  const projectCoordinatesErrorMessage =
    hasLat && hasLng && latIsValid && lngIsValid && !coordinatesWithinSwitzerland
      ? 'Please enter coordinates inside Switzerland!'
      : 'Please enter valid latitude and longitude!';
  const mapCenter = useMemo<[number, number]>(() => {
    if (Number.isFinite(latValue) && Number.isFinite(lngValue)) {
      return [latValue, lngValue];
    }
    return [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng];
  }, [latValue, lngValue]);
  const showMarker = Number.isFinite(latValue) && Number.isFinite(lngValue);
  const mapZoom = showMarker ? PROJECT_LOCATION_FOCUS_ZOOM : DEFAULT_MAP_ZOOM;

  useEffect(() => {
    if (!coordinatesValid) {
      setIsProjectMunicipalityValid(false);
      if (shouldShowCoordinatesError || showProjectValidationErrors) {
        setProjectMunicipalityErrorMessage(projectCoordinatesErrorMessage);
      } else {
        setProjectMunicipalityErrorMessage('');
      }
      return;
    }

    setIsProjectMunicipalityValid(true);
    setProjectMunicipalityErrorMessage('');
  }, [
    coordinatesValid,
    coordinatesWithinSwitzerland,
    hasLat,
    hasLng,
    latIsValid,
    lngIsValid,
    projectCoordinatesErrorMessage,
    shouldShowCoordinatesError,
    showProjectValidationErrors,
    setIsProjectMunicipalityValid,
    setProjectMunicipalityErrorMessage,
  ]);

  useEffect(() => {
    if (!showProjectValidationErrors) return;
    if (!project?.name?.trim()) {
      setIsProjectNameValid(false);
      setProjectNameErrorMessage('Please enter a project name!');
    }
  }, [project?.name, setIsProjectNameValid, setProjectNameErrorMessage, showProjectValidationErrors]);

  return (
    <>
      <ProjectCreationSplitLayout
        className="grid-settings-step"
        leftClassName="grid-settings-step__sidebar"
        rightClassName="grid-settings-step__preview grid-settings-step__preview--project"
        left={
          <div style={{ overflow: 'visible' }}>
            <div style={{ overflow: 'visible' }}>
              <div>
                <label className="relative block text-sm mb-2" htmlFor="name">
                  Project Name <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <TextInput name="name" value={project?.name} onChange={handleInputsChange} required />
                {showProjectValidationErrors && !isProjectNameValid && (
                  <p style={{ color: '#d32f2f', marginTop: '8px' }}>{projectNameErrorMessage}</p>
                )}
              </div>
            </div>
            {/* <Grid cols='2'>
          <Select
            label={
              <>
                Analysis Type
                <span className='asterisk-color'> *</span>
                <CustomTooltip
                  title={
                    <>
                      <p>
                        Site Analysis: To perform the volumetric analysis of a
                        site.
                      </p>
                      <p>
                        Land Analysis: To perform the 3D land analysis of an area
                        of land.
                      </p>
                    </>
                  }
                />
              </>
            }
            default={project.analysisType}
            options={[
              { value: 'site', label: 'Site' },
              { value: 'land', label: 'Land' },
            ]}
            onChange={handleChangeAnalysisType}
          />
        </Grid> */}

            {project.analysisType === 'site' && (
              <>
                <div style={{ clear: 'both', overflow: 'visible', marginTop: '18px' }}>
                  <div>
                    <label className="relative block text-sm mb-2" htmlFor="address">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span>
                          Location <span style={{ color: '#d32f2f' }}>*</span>
                        </span>
                        <Tooltip title="Provide address, coordinates or click on the map" arrow>
                          <InfoOutlinedIcon sx={{ color: '#000000', fontSize: 16 }} />
                        </Tooltip>
                      </span>
                    </label>
                    <div style={{ position: 'relative' }} ref={addressDropdownRef}>
                      <input
                        className="project-street-input"
                        id="address"
                        name="address"
                        style={{ width: '100%' }}
                        placeholder="Street 1, Zurich or 46.81, 8.21"
                        value={addressValue}
                        autoComplete="off"
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="none"
                        onChange={(evt) => handleAddressChange(evt.target.value)}
                      />
                      {isPlacePredictionsLoading && <CircularProgress size={18} style={{ marginTop: '6px' }} />}
                      {isListOpen && !isPlacePredictionsLoading && placePredictions.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '100%',
                            zIndex: 20,
                            background: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            maxHeight: '240px',
                            overflowY: 'auto',
                            marginTop: '6px',
                          }}
                        >
                          <List dense>
                            {placePredictions.map((item) => (
                              <ListItem key={item.place_id} disablePadding>
                                <ListItemButton onClick={() => handleAddressSelect(item.description, item.place_id)}>
                                  <ListItemText primary={item.description} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </div>
                      )}
                    </div>
                    {!isProjectMunicipalityValid && projectMunicipalityErrorMessage && (
                      <p
                        style={{
                          background: 'transparent',
                          color: '#d32f2f',
                          marginTop: '8px',
                          padding: 0,
                          borderRadius: 0,
                        }}
                      >
                        {projectMunicipalityErrorMessage}
                      </p>
                    )}
                    {showCoordinatesError && !projectMunicipalityErrorMessage && (
                      <p
                        style={{
                          background: 'transparent',
                          color: '#d32f2f',
                          marginTop: '8px',
                          padding: 0,
                          borderRadius: 0,
                        }}
                      >
                        Please enter valid coordinates!
                      </p>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '20px',
                    border: '1px solid #2f80ed',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    background: 'rgba(47, 128, 237, 0.08)',
                    color: '#2b2b2b',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: '#2f80ed', marginTop: '2px' }} />
                  <div>
                    <p style={{ margin: 0, lineHeight: 1.45 }}>
                      Project location cannot be changed. Your site must have at least one point within 100m of the
                      project location (blue circle).
                    </p>
                  </div>
                </div>
                <details style={{ marginTop: '18px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 400 }}>Define Project Specs</summary>
                  <div style={{ display: 'grid', gap: '12px', marginTop: '12px', marginLeft: '16px' }}>
                    <label>
                      <span className="relative block text-sm mb-2">Number of Buildings</span>
                      <UMInputNumber
                        min={1}
                        value={project.numberOfBuildings}
                        maxDecimals={0}
                        onChange={(_, value) => handleProjectSpecNumberChange('numberOfBuildings', value)}
                        width={84}
                      />
                    </label>
                    <label>
                      <span className="relative block text-sm mb-2">Buildings Use</span>
                      <RadioGroup
                        row
                        sx={{ marginTop: '-6px' }}
                        value={project.buildingUse}
                        onChange={(event) =>
                          setProject((current) => ({
                            ...current,
                            buildingUse: event.target.value as 'Residential' | 'Office',
                          }))
                        }
                      >
                        <FormControlLabel value="Residential" control={<Radio size="small" />} label="Residential" />
                        <FormControlLabel value="Office" control={<Radio size="small" />} label="Office" />
                      </RadioGroup>
                    </label>
                    <label>
                      <span className="relative flex items-center gap-1.5 text-sm mb-2">
                        <span>Gross Floor Surface</span>
                        <Tooltip
                          title="Total surface of spaces above ground, including the horizontal section of walls"
                          arrow
                        >
                          <InfoOutlinedIcon sx={{ color: '#000000', fontSize: 16 }} />
                        </Tooltip>
                      </span>
                      <span className="flex items-center gap-2">
                        <UMInputNumber
                          min={0}
                          value={project.grossFloorSurface ?? ''}
                          maxDecimals={1}
                          onChange={(_, value) => handleProjectSpecNumberChange('grossFloorSurface', value)}
                          width={84}
                        />
                        <span className="text-sm">[m²]</span>
                      </span>
                    </label>
                    <label>
                      <span className="relative block text-sm mb-2">Number of Floors</span>
                      <UMInputNumber
                        min={1}
                        value={project.numberOfFloors ?? ''}
                        maxDecimals={0}
                        onChange={(_, value) => handleProjectSpecNumberChange('numberOfFloors', value)}
                        width={84}
                      />
                    </label>
                  </div>
                </details>
              </>
            )}
          </div>
        }
        right={
          <div className="grid-map-shell">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              className="map-container-component"
              style={{ height: 'var(--project-creation-map-height)', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapUpdater center={mapCenter} zoom={mapZoom} />
              <MapClickHandler onPick={handleMapPick} />
              {showMarker && (
                <>
                  <Circle
                    center={mapCenter}
                    radius={PROJECT_LOCATION_RADIUS_METERS}
                    pathOptions={{ className: 'project-location-radius', weight: 2 }}
                  />
                  <Marker position={mapCenter} />
                </>
              )}
            </MapContainer>
          </div>
        }
      />
    </>
  );
};

export default MandatoryFields;
